import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useAuth } from '../../contexts/auth/useAuth';

interface AuthGuardType {
  children: React.ReactNode;
  adminOnly?: boolean;
  /** When set with adminOnly, the account selected in the navbar must match "admin". */
  requireSelectedAccount?: string;
}

// For routes that can only be accessed by authenticated users
function AuthGuard({
  children,
  adminOnly = false,
  requireSelectedAccount,
}: AuthGuardType) {
  const { isAuthenticated } = useAuth();
  const { account, loggedInAccount } = useAuthContext();
  const { pathname, search } = useLocation();

  // Not authenticated - send to sign in, coming back here afterwards. "/" is
  // where sign-in goes anyway, so it needs no returnTo.
  if (!isAuthenticated) {
    const returnTo = pathname + search;
    return (
      <Navigate
        to={
          returnTo === '/'
            ? '/welcome'
            : `/welcome?returnTo=${encodeURIComponent(returnTo)}`
        }
        replace
      />
    );
  }

  // Admin-only route check
  if (adminOnly && loggedInAccount !== 'admin') {
    return <Navigate to="/" />;
  }

  if (
    adminOnly &&
    requireSelectedAccount &&
    account !== requireSelectedAccount
  ) {
    return <Navigate to="/" />;
  }

  return <React.Fragment>{children}</React.Fragment>;
}

export default AuthGuard;
